import { useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix default marker icons under webpack (Leaflet ships images as URLs bundled from CDN)
const iconRetina = "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png";
const icon = "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png";
const shadow = "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png";
L.Icon.Default.mergeOptions({ iconRetinaUrl: iconRetina, iconUrl: icon, shadowUrl: shadow });

const DISCIPLINE_LABEL = {
  nordic_yoga: "Nordic Yoga",
  marche_nordique: "Marche Nordique",
  stage: "Stage",
};

function fmt(d) {
  return new Date(d).toLocaleString("fr-FR", { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" });
}

/**
 * SessionsMap — Interactive Leaflet map of session locations.
 * Props: sessions (list with optional lat/lng), height (CSS string), onRegister? (public/private variant).
 */
export default function SessionsMap({ sessions = [], height = "420px", onRegister = null }) {
  const withCoords = useMemo(() => sessions.filter(s => typeof s.lat === "number" && typeof s.lng === "number"), [sessions]);
  const center = useMemo(() => {
    if (!withCoords.length) return [50.8503, 4.3517]; // Brussels default
    const avgLat = withCoords.reduce((a, s) => a + s.lat, 0) / withCoords.length;
    const avgLng = withCoords.reduce((a, s) => a + s.lng, 0) / withCoords.length;
    return [avgLat, avgLng];
  }, [withCoords]);

  if (!withCoords.length) {
    return (
      <div className="card-warm p-8 text-center text-[var(--muted-text)]" data-testid="sessions-map-empty">
        Aucun lieu géolocalisé pour l'instant. Ajoutez une latitude/longitude à vos séances pour les afficher ici.
      </div>
    );
  }

  return (
    <div className="rounded-2xl overflow-hidden shadow-sm border border-slate-200" style={{ height }} data-testid="sessions-map">
      <MapContainer center={center} zoom={withCoords.length === 1 ? 13 : 9} scrollWheelZoom={false} style={{ height: "100%", width: "100%" }}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {withCoords.map(s => (
          <Marker key={s.id} position={[s.lat, s.lng]}>
            <Popup>
              <div style={{ minWidth: 220 }}>
                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 18, fontWeight: 600, color: "#2E5A44" }}>{s.title}</div>
                <div style={{ fontSize: 11, color: "#7A8F81", marginTop: 2, textTransform: "uppercase", letterSpacing: 0.5 }}>{DISCIPLINE_LABEL[s.discipline] || s.discipline}</div>
                <div style={{ fontSize: 12, color: "#5A6B62", marginTop: 8 }}>{fmt(s.start_at)}</div>
                <div style={{ fontSize: 12, color: "#5A6B62" }}>{s.location}</div>
                <div style={{ marginTop: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontWeight: 600, color: "#C46A4B" }}>{s.price_eur} €</span>
                  <span style={{ fontSize: 11, color: "#7A8F81" }}>{s.places_left ?? "—"} places</span>
                </div>
                {onRegister && s.places_left > 0 && (
                  <button
                    onClick={() => onRegister(s.id)}
                    style={{ marginTop: 10, width: "100%", padding: "6px 12px", background: "#2E5A44", color: "white", borderRadius: 6, border: 0, cursor: "pointer" }}
                    data-testid={`map-register-${s.id}`}
                  >Réserver</button>
                )}
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}
